import React from 'react'
import { Stack, Card, Button, Avatar, alpha } from '@mui/material'
import { ImageIcon } from '@/icon'
import Text from '@/components/Text'
import { styled, keyframes } from '@mui/material/styles'
import type { CSSProperties } from 'react'
import { boxShadow, primary } from '@/constant'
import id_icon from '@/asset/img/id_icon.webp'

export const FloaterWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: 'true' | 'false' }>(({ open = 'true', theme }) => ({
  display: 'flex',
  color: 'white',
  position: 'absolute',
  top: '0',
  left: '0',
  '& > .flouter-badge': {
    position: 'absolute',
    left: '32px',
    top: '8px',
    zIndex: 2,
    transform: 'translateY(0px)',
    opacity: 1,
    transition: 'all 0.2s ease-in-out',
  },
  '& > .floater-nav': {
    display: 'flex',
    color: 'white',
    background: theme.palette.primary.main,
    boxShadow: `0px 0px 30px 0px  ${alpha(theme.palette.primary.main, 0.36)}`,
    borderRadius: '50px',
    paddingTop: '24px',
    paddingLeft: '24px',
    width: '56px',
    height: '56px',
    position: 'absolute',
    marginTop: '-20px',
    marginLeft: '-20px',
    overflow: 'hidden',
    transition: 'all 0.1s ease-in-out 0.4s',
    zIndex: 2,
    // "& > svg": {
    //   display: "none",
    // },
  },
  '& > .floater-panel': {
    display: 'flex',
    color: 'white',
    zIndex: 1,
    width: '372px',
    height: '0px',
    borderRadius: '0 2% 2% 0',
    backgroundColor: '#F8FAFB',
    overflow: 'hidden',
    position: 'absolute',
    boxShadow: '0px 40px 60px 0px rgba(0,0,0,0.16)',
    transition: 'height 0.2s ease-in-out, width 0.2s ease-in-out 0.2s',
  },
  '&:hover': {
    ...(open === 'true' && {
      '& .floater-nav': {
        animationName: bounceSize,
        animationDuration: '0.8s',
        animationFillMode: 'forwards',
        width: '64px',
        height: '64px',
        // transition: "width 0s ease-in-out 0.8s",
        '& > svg': {
          display: 'block',
        },
      },
      '& .floater-panel': {
        height: '100vh',
        width: '372px',
        transition: 'height 0.2s ease-in-out',
      },
      '& > .flouter-badge': {
        opacity: 0,
        transform: 'translateY(-20px)',
        transition: 'all 0.2s ease-in-out',
      },
    }),
  },
}))

const bounceSize = keyframes({
  '0%': {
    width: '60px',
    height: '60px',
  },
  '30%': {
    width: '74px',
    height: '74px',
  },
  '45%': {
    width: '60px',
    height: '60px',
  },
  '65%': {
    width: '70px',
    height: '70px',
  },
  '100%': {
    width: '64px',
    height: '64px',
  },
})

export const InfoCardBase = styled(Card)(() => ({
  backgroundImage: 'none',
  backgroundColor: '#F8FAFB',
  backgroundSize: 'fill',
  backgroundPositionX: 'center',
  backgroundRepeat: 'no-repeat',
  height: '100%',
  width: '100%',
  padding: '30px 24px 12px',
  paddingTop: '30px',
}))

export const Row = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
}))

export const AppList = styled(Row)(() => ({
  flexWrap: 'wrap',
  gap: '8px',
}))

export const AppBox = ({ icon, name, onClick }: { icon: string; name: string; onClick?: () => void }) => {
  return (
    <Stack
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        width: '96px',
        transition: 'all 0.2s ease-in-out',
        padding: '8px',
        cursor: 'pointer',
        '&:hover': {
          background: 'linear-gradient(138deg, rgba(175,254,148,0.1) 0%, rgba(144,184,255,0.1) 100%)',
          transition: 'all 0.2s ease-in-out',
        },
      }}
      onClick={onClick}
    >
      <ImageIcon src={icon} sx={{ width: '40px', aspectRatio: '1' }} />
      <Text
        weight='thin'
        sx={{
          width: '100%',
          fontSize: '14px',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {name}
      </Text>
    </Stack>
  )
}

export const IndicatorIcon = styled('div')(() => ({
  display: 'inline-block',
  width: 8,
  height: 3,
}))

export const indicatorIconButtonStyle: CSSProperties = {
  borderRadius: 0,
  background: '#ccc',
  marginRight: 10,
}

export const activeIndicatorIconButtonStyle: CSSProperties = {
  background: 'linear-gradient(90deg, #52C41A 0%, #11AF60 100%)',
  boxShadow: '0px 3px 6px 0px rgba(82,196,26,0.5)',
  width: 20,
  transition: `all ${0.3}s linear`,
}

export const IdCard = styled(Stack)(() => ({
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPositionX: '48px',
  padding: '16px 0px',
  paddingBottom: 0,
  width: '100%',
}))

export const UserIdButton = styled(Button)(() => ({
  backgroundColor: '#F7F7F7FF',
  color: '#717579FF',
  padding: '8px 16px',
}))

export const OrgIcon = styled(Avatar)(() => ({
  width: '24px',
  height: '24px',
  fontSize: '12px',
}))

export const OrgCard = styled(Row)(() => ({
  backgroundColor: 'transparent',
  justifyContent: 'space-between',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  padding: '12px',
  '&:hover': {
    backgroundColor: '#52C41A10',
    transition: 'all 0.2s ease-in-out',
    '& > div > div': {
      color: primary,
      transition: 'all 0.2s ease-in-out',
    },
    '& > div > div > div': {
      color: primary,
      transition: 'all 0.2s ease-in-out',
    },
  },
}))

export const OrgDisplay = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: 'true' | 'false' }>(({ open }) => ({
  display: 'block',
  height: '42px',
  minHeight: '42px',
  background: '#fff',
  position: 'relative',
  borderRadius: '4px',
  ...(open === 'true' && {
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out',
  }),
  '&:hover': {
    boxShadow,
    transition: 'all 0.2s ease-in-out',
  },
}))

export const OrgsCard = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: 'true' | 'false' }>(({ open }) => ({
  display: 'block',
  padding: '12px',
  fontSize: '14px',
  background: '#fff',
  height: '42px',
  width: '100%',
  position: 'absolute',
  borderRadius: '4px',
  top: 0,
  left: 0,
  zIndex: 2,
  transition: 'all 0.2s ease-in-out',
  ...(open === 'true' && {
    height: '60vh',
    boxShadow,
    transition: 'height 0.2s ease-in-out ,z-index 0.2s ease-in-out ,box-shadow 0.2s ease-in-out',
  }),
}))

export const IdIcon = () => <ImageIcon src={id_icon} />

export const IdMenuButton = styled('div')({
  display: 'flex',
  borderRadius: '4px',
  cursor: 'pointer',
  alignItems: 'center',
  fontSize: '14px',
  gap: '8px',
  height: '46px',
  width: '100%',
  padding: '4px 12px',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: '#F3F4F7FF',
  },
  '& > svg': {
    fontSize: '14px',
  },
})
